// Notification types for frontend

export type NotificationType =
  | 'material_review'
  | 'ktp_verification'
  | 'enrollment_payment'
  | 'withdrawal_request'
  | 'commission_earned'
  | 'certificate_issued'
  | 'system_announcement'
  | 'quiz_review'
  | 'quiz_graded'
  | 'assignment_review'
  | 'membership_upgrade';

export interface Notification {
  id: string;
  recipientUserId?: string;
  recipientRole?: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  readAt?: string;
  actionLink?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface NotificationFilter {
  type?: NotificationType;
  is_read?: boolean;
  limit?: number;
  offset?: number;
}

export interface NotificationListResponse {
  notifications: Notification[];
  total: number;
  limit: number;
  offset: number;
}

export interface UnreadCountResponse {
  unreadCount: number;
}

// Helper to get notification icon based on type
export const getNotificationIcon = (type: NotificationType): string => {
  switch (type) {
    case 'material_review':
      return 'rate_review';
    case 'ktp_verification':
      return 'badge';
    case 'enrollment_payment':
      return 'payment';
    case 'withdrawal_request':
      return 'account_balance';
    case 'commission_earned':
      return 'monetization_on';
    case 'certificate_issued':
      return 'workspace_premium';
    case 'system_announcement':
      return 'info';
    case 'quiz_review':
      return 'quiz';
    case 'quiz_graded':
      return 'check_circle';
    case 'assignment_review':
      return 'assignment';
    case 'membership_upgrade':
      return 'emoji_events';
    default:
      return 'notifications';
  }
};

// Helper to get notification color based on type
export const getNotificationColor = (type: NotificationType): string => {
  switch (type) {
    case 'material_review':
      return 'text-blue-500 bg-blue-500/10';
    case 'ktp_verification':
      return 'text-amber-500 bg-amber-500/10';
    case 'enrollment_payment':
      return 'text-emerald-500 bg-emerald-500/10';
    case 'withdrawal_request':
      return 'text-orange-500 bg-orange-500/10';
    case 'commission_earned':
      return 'text-green-500 bg-green-500/10';
    case 'certificate_issued':
      return 'text-purple-500 bg-purple-500/10';
    case 'system_announcement':
      return 'text-slate-500 bg-slate-500/10';
    case 'quiz_review':
      return 'text-amber-500 bg-amber-500/10';
    case 'quiz_graded':
      return 'text-emerald-500 bg-emerald-500/10';
    case 'assignment_review':
      return 'text-violet-500 bg-violet-500/10';
    case 'membership_upgrade':
      return 'text-yellow-500 bg-yellow-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
};

// Helper to format time ago
export const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Baru saja';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} menit lalu`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} jam lalu`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} hari lalu`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} minggu lalu`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} bulan lalu`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} tahun lalu`;
};